import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";

const BackendURL = config.backendUrl;

/** Server-side PDF uses Puppeteer; surface a clear message when QA/Linux is missing Chrome/Chromium. */
function formatCreateQuotationError(err) {
  const data = err.response?.data;
  let raw = "";
  if (typeof data === "string") {
    raw = data;
  } else if (data && typeof data === "object") {
    raw = data.message || data.error || "";
    if (!raw) {
      try {
        raw = JSON.stringify(data);
      } catch (_) {
        raw = String(data);
      }
    }
  }
  if (!raw) raw = err.message || String(err);

  if (/puppeteer|Could not find Chrome|\.cache\/puppeteer|pptr\.dev/i.test(raw)) {
    return {
      message:
        "Quotation PDF could not be generated because Chrome/Chromium is not installed on the application server (QA). Ask DevOps to install Chromium or Google Chrome on the API host, set environment variable PUPPETEER_EXECUTABLE_PATH to the browser binary, or run on the server after deploy: npx puppeteer browsers install chrome",
      error: raw,
    };
  }

  if (typeof data === "object" && data !== null && (data.message || data.error)) {
    return data;
  }
  return { message: raw, error: raw };
}

export const createQuotation = createAsyncThunk(
  "generateQuotation/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BackendURL}/generatequotation/quotations`, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(formatCreateQuotationError(err));
    }
  }
);

export const editGenerateQuotation = createAsyncThunk(
  "generateQuotation/edit",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${BackendURL}/generatequotation/edit-quotation`,
        data
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchAllQuotations = createAsyncThunk(
  "generateQuotation/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BackendURL}/generatequotation/quotations`);
      console.log("responseresponse", response.data);

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const downloadQuotationFile = createAsyncThunk(
  "generateQuotation/downloadFile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${BackendURL}/generatequotation/download-quotation/${id}`,
        {
          responseType: "blob", // Important for file downloads
        }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const downloadUrl = window.URL.createObjectURL(blob);
      return { downloadUrl, contentType: response.headers['content-type'] };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const sendQuotationToClient = createAsyncThunk(
  "generateQuotation/sendToClient",
  async ({ quotationId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${BackendURL}/generatequotation/email-quotation`,
        {
          quotationId
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const sendQuotationToSalesPresales = createAsyncThunk(
  "generateQuotation/sendToSalesPresales",
  async ({ quotationId, recipientEmail }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${BackendURL}/generatequotation/email-quotation`,
        {
          quotationId,
          recipientEmail // Send to Sales Presales person instead of client
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
// export const sendQuotationToClient = createAsyncThunk(
//   "generateQuotation/sendToClient",
//   async ({ leadid, templatedata }, { rejectWithValue }) => {
//     try {
//       const response = await axiosInstance.post(
//         `${BackendURL}/generatequotation/email-quotation`,
//         {
//           leadid,
//           templatedata,
//         }
//       );
//       return response.data;
//     } catch (err) {
//       return rejectWithValue(err.response?.data || err.message);
//     }
//   }
// );


export const updateQuotation = createAsyncThunk(
  "generateQuotation/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${BackendURL}/${id}`, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteQuotation = createAsyncThunk(
  "generateQuotation/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BackendURL}/generatequotation/quotations/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const generateQuotationSlice = createSlice({
  name: "generateQuotation",
  initialState: {
    quotations: [],
    loading: false,
    error: null,
    successMessage: null,
    convertedFileUrl: null,
  },
  reducers: {
    clearQuotationMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(createQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuotation.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations.unshift(action.payload.data);
        state.successMessage = action.payload.message;
      })
      .addCase(createQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(editGenerateQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editGenerateQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const updatedQuotation = action.payload.data;
        if (updatedQuotation._id) {
          const index = state.quotations.findIndex(
            (q) => q._id === updatedQuotation._id
          );
          if (index !== -1) {
            state.quotations[index] = updatedQuotation;
          } else {
            state.quotations.unshift(updatedQuotation);
          }
        } else {
          state.quotations.unshift(updatedQuotation);
        }
        state.successMessage = action.payload.message;
      })
      .addCase(editGenerateQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllQuotations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllQuotations.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = action.payload;
      })
      .addCase(fetchAllQuotations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.data;
        const index = state.quotations.findIndex(q => q._id === updated._id);
        if (index !== -1) state.quotations[index] = updated;
        state.successMessage = action.payload.message;
      })
      .addCase(updateQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteQuotation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQuotation.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = state.quotations.filter(q => q._id !== action.payload);
        state.successMessage = "Quotation deleted successfully";
      })
      .addCase(deleteQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(downloadQuotationFile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.convertedFileUrl = null;
      })
      .addCase(downloadQuotationFile.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.convertedFileUrl = action.payload.downloadUrl;
      })
      .addCase(downloadQuotationFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.convertedFileUrl = null;
      })

      .addCase(sendQuotationToClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendQuotationToClient.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(sendQuotationToClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(sendQuotationToSalesPresales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendQuotationToSalesPresales.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(sendQuotationToSalesPresales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },
});

export const { clearQuotationMessages } = generateQuotationSlice.actions;
export default generateQuotationSlice.reducer;
